"use client";
import * as React from "react";
import { cn } from "@repo/utils/cn";
import { VariantProps } from "tailwind-variants";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "../AiDisclaimer";
import { SectionHeader, SectionHeaderProps } from "../SectionHeader";
import { sectionWrapper } from "../sectionWrappers";
import { LinedListItem, LinedListItemProps } from "./LinedListItem";
import {
  LinedListItemDetailed,
  LinedListItemDetailedProps,
} from "./LinedListItemDetailed";
import { Accordion, AccordionProps } from "../Accordion";
import { Button } from "../Button";

export type LinedListSectionItemType =
  | (LinedListItemProps & { component: "linedListItem" })
  | (LinedListItemDetailedProps & { component: "linedListItemDetailed" })
  | (AccordionProps & { component: "accordion"; _uid: string });

interface SectionImage {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  aspectRatio?: string;
}

export interface LinedListSectionProps
  extends VariantProps<typeof sectionWrapper> {
  typography?: SectionHeaderProps | null;
  items?: LinedListSectionItemType[];
  type?: "default" | "split";
  image?: SectionImage | null;
  className?: string;
  pageType?: "default" | "location";
}

export function LinedListSection({
  typography,
  items = [],
  type = "default",
  image,
  className,
  backgroundColor = "none",
  topPadding = "default",
  bottomPadding = "default",
  pageType = "default",
}: LinedListSectionProps) {
  // For location pages, apply h5 and balanced text wrapping to title
  const enhancedTypography = React.useMemo(() => {
    if (!typography) return typography;

    if (pageType === "location") {
      return {
        ...typography,
        title: typography.title
          ? {
              ...typography.title,
              fontSize: "h5" as const,
              textWrap: "balance" as const,
            }
          : typography.title,
      };
    }

    return typography;
  }, [pageType, typography]);

  const renderItem = (item: LinedListSectionItemType, index: number) => {
    switch (item.component) {
      case "linedListItem":
        return <LinedListItem key={item._uid} {...item} />;
      case "linedListItemDetailed":
        return <LinedListItemDetailed key={item._uid} {...item} />;
      case "accordion":
        return (
          <div key={item._uid} className="uipm:relative uipm:w-full">
            <div
              className={cn(
                "uipm:py-[var(--container-margin)] uipm:px-0 uipm:w-full",
                index === 0 && "uipm:pt-[var(--container-margin)]",
              )}
            >
              <Accordion
                items={item.items}
                defaultOpenIndex={item.defaultOpenIndex}
                variant="location-inline"
                className={item.className}
                onItemToggle={item.onItemToggle}
              />
            </div>
            <div
              aria-hidden="true"
              className="uipm:absolute uipm:border-b uipm:border-[var(--color-color-light)] uipm:inset-0 uipm:pointer-events-none"
            />
          </div>
        );
      default:
        return null;
    }
  };

  const isSplit = type === "split";
  const hasAccordions = items.some((item) => item.component === "accordion");
  const shouldUseSplitLayout = isSplit || (type === "default" && hasAccordions);

  // In split mode, extract buttons from typography to render below image
  const splitButtons = shouldUseSplitLayout
    ? enhancedTypography?.buttons
    : null;
  const headerTypography = React.useMemo(() => {
    if (!enhancedTypography || !splitButtons?.length) return enhancedTypography;
    return { ...enhancedTypography, buttons: undefined };
  }, [enhancedTypography, splitButtons]);

  return (
    <section
      className={cn(
        sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
          pageType,
        }),
        className,
      )}
    >
      <div className="uipm:max-w-[920px] uipm:mx-auto uipm:w-full">
        {shouldUseSplitLayout ? (
          <div className="uipm:flex uipm:flex-col uipm:gap-[var(--container-section-top)] uipm:items-start uipm:md:flex-row uipm:md:gap-[var(--container-gutter)]">
            {/* Left column: header on top, image+button at bottom */}
            <div className="uipm:flex uipm:flex-col uipm:items-start uipm:w-full uipm:md:w-[330px] uipm:md:shrink-0 uipm:md:pr-[var(--container-gutter)] uipm:md:justify-between uipm:md:self-stretch">
              {headerTypography && (
                <div className="uipm:flex uipm:flex-1 uipm:gap-[var(--margin-margin-xs)] uipm:items-start uipm:pb-[var(--margin-margin-sm)] uipm:w-full">
                  <SectionHeader
                    {...headerTypography}
                    variant={pageType === "location" ? "location" : "default"}
                  />
                </div>
              )}
              {(image || (splitButtons && splitButtons.length > 0)) && (
                <div className="uipm:flex uipm:flex-col uipm:gap-[var(--margin-margin-sm)] uipm:items-start uipm:shrink-0 uipm:w-full">
                  {image && (
                    <div
                      className="uipm:relative uipm:w-full uipm:overflow-hidden uipm:rounded-[var(--radius-radius-lg)]"
                      style={{
                        aspectRatio: image.aspectRatio || "1/1",
                      }}
                    >
                      <Image
                        src={image.src}
                        alt={parseAiAlt(image.alt).cleanAlt}
                        fill
                        sizes="(min-width: 834px) 306px, 100vw"
                        className="uipm:object-cover"
                      />
                      <AiDisclaimerForAlt alt={image.alt} src={image.src} />
                    </div>
                  )}
                  {splitButtons
                    ?.filter(Boolean)
                    .map((btn, i) => (
                      <Button
                        key={i}
                        {...btn}
                        size="small"
                        className={cn("uipm:w-full", btn.className)}
                      />
                    ))}
                </div>
              )}
            </div>

            {/* Right column: list items */}
            <div className="uipm:border-t uipm:border-[var(--color-color-light)] uipm:flex uipm:flex-col uipm:gap-[var(--container-margin)] uipm:items-start uipm:pt-[var(--container-gutter)] uipm:w-full uipm:md:flex-1 uipm:min-w-0">
              {items.map(renderItem)}
            </div>
          </div>
        ) : (
          <div className="uipm:flex uipm:flex-col uipm:gap-[var(--container-section-top)] uipm:items-start">
            {enhancedTypography && (
              <div className="uipm:flex uipm:gap-[var(--padding-padding-xs)] uipm:items-start uipm:w-full">
                <SectionHeader
                  {...enhancedTypography}
                  variant={pageType === "location" ? "location" : "default"}
                />
              </div>
            )}

            <div className="uipm:flex uipm:flex-col uipm:gap-[var(--container-margin)] uipm:items-start uipm:pb-0 uipm:px-0 uipm:w-full">
              <div className="uipm:border-t uipm:border-[var(--color-color-light)] uipm:relative uipm:w-full">
                {items.map(renderItem)}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
